import { useCallback, useMemo, useState } from 'react';

import { DateTools, treezorLocaleConverters } from '@treezor/design-system';

import { useCreateUserContext, ICreateUserInfo } from '@/features/user/contexts/CreateUserContext';

import { selectMembersTaxResidences, selectProfileByUserId, selectMembersKycDocuments } from '@/store/selectors';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useI18nLocale } from '@/hooks/useI18nLocale';

import { createChildUser } from '@/store/actions/profile.actions';

import { useMatomo } from '@/features/matomo/useMatomo';

import { useAddTaxResidences } from './useTaxResidences';
import { useCreateUserKycDocuments } from './useCreateUserKycDocuments';

export function useMembers(userId?: number) {
  const locale = useI18nLocale();
  const language = treezorLocaleConverters[locale];

  const dispatch = useAppDispatch();

  const { user } = useCreateUserContext();

  const { addChildTaxResidences } = useAddTaxResidences(userId);

  const { addChildKycDocuments } = useCreateUserKycDocuments();
  const { triggerEvent } = useMatomo();

  const [, setMemberIds] = useState<(number | undefined)[]>([]);

  const members = useAppSelector((state) => selectProfileByUserId(state, userId)?.state?.children?.create);

  const membersTaxResidences = useAppSelector((state) => selectMembersTaxResidences(state, userId));

  const membersKycDocuments = useAppSelector((state) => selectMembersKycDocuments(state, userId));

  const isMemberCreated =
    user?.members?.length > 0
      ? members?.state && Object.values(members.state).every((state) => state.isLoading === false)
      : true;

  const isMemberLoading = Object.values(members?.state ?? {}).some((state) => state.isLoading === true);

  const isMembersTaxResidencesCreated = useMemo(
    () =>
      membersTaxResidences?.state?.length > 0
        ? membersTaxResidences?.create?.state &&
          Object.values(membersTaxResidences?.create?.state).every((state) => state.isLoading === false)
        : true,
    [membersTaxResidences],
  );

  const isMembersTaxResidencesLoading = useMemo(
    () => Object.values(membersTaxResidences?.create?.state ?? {}).some((state) => state.isLoading === true),
    [membersTaxResidences],
  );

  const isMembersDocumentsUploaded = useMemo(
    () =>
      membersKycDocuments?.state?.length > 0
        ? membersKycDocuments?.create?.state &&
          Object.values(membersKycDocuments.create.state).every((state) => state.isLoading === false)
        : true,
    [membersKycDocuments],
  );

  const isMembersDocumentsLoading = useMemo(
    () => Object.values(membersKycDocuments?.create?.state ?? {}).some((state) => state.isLoading === true),
    [membersKycDocuments],
  );

  const onAddMember = useCallback(
    ({ entityType, taxResidences, kycDocuments, ...rest }: ICreateUserInfo, index: number) => {
      triggerEvent('user-children', { 'user-children-action': 'submit' });
      dispatch(
        createChildUser(rest, `batch-${index}`, (memberId: number) => {
          addChildTaxResidences(taxResidences, memberId, rest.parentUserId);
          addChildKycDocuments(kycDocuments, rest.parentUserId, memberId);
          setMemberIds((current) => [...current, memberId]);
        }),
      );
    },
    [dispatch, addChildTaxResidences, addChildKycDocuments, triggerEvent],
  );

  const addMembers = useCallback(
    (members: ICreateUserInfo[], userId: number) => {
      (members || []).map(({ isSameAsPrincipalAddress, roles, birthday, ...members }, index) => {
        const birthdayDateApiFormat = birthday
          ? DateTools.format(DateTools.localStringToDate(birthday, language), 'api-date')
          : undefined;

        const membersToAdd = {
          ...members,
          parentUserId: userId,
          userTypeId: 1,
          birthday: birthdayDateApiFormat,
          ...(user.distributionCountry ? { distributionCountry: user.distributionCountry } : {}),
        };

        onAddMember(membersToAdd, index);
      });
    },
    [language, onAddMember, user.distributionCountry],
  );

  return {
    onAddMember,
    addMembers,
    isMemberLoading,
    isMemberCreated,
    isMembersTaxResidencesCreated,
    isMembersDocumentsUploaded,
    isMembersTaxResidencesLoading,
    isMembersDocumentsLoading,
  };
}
